import { useOktaAuth } from "@okta/okta-react";
import { Box, CircularProgress, Paper, TableContainer, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { formatDate } from "../utils/FormatDate";
import { FormattedDataGrid } from "../utils/FormattedDataGrid";

const WSU_RED = "#A60F2D";

export const OktaLog = ({ userId }) => {
  const [state, setState] = useState({ loaded: false, error: null, data: null });
  const { authState } = useOktaAuth();

  const columns = [
    { field: "id", headerName: "ID", headerClassName: "wsu-theme-header", headerAlign: "center", align: "center", width: 150 },
    { field: "username", headerName: "User Name", headerClassName: "wsu-theme-header", headerAlign: "center", align: "center", width: 250 },
    { field: "event_action", headerName: "Event Action", headerClassName: "wsu-theme-header", headerAlign: "center", align: "center", width: 150 },
    { field: "event_actor", headerName: "Event Actor", headerClassName: "wsu-theme-header", headerAlign: "center", align: "center", width: 250 },
    {
      field: "ip_address",
      headerName: "IP Address",
      headerClassName: "wsu-theme-header",
      headerAlign: "center",
      align: "center",
      minWidth: 400,
      flex: 1,
      renderCell: (params) => (
        <center>
          <div style={{ padding: "8px 12px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.6, width: "100%", boxSizing: "border-box" }}>
            <table>
              <tr><td>Address: </td><td>{params.value.address}</td></tr>
              <tr><td>Is Proxy/VPN: </td><td>{params.value.isProxy}</td></tr>
              <tr><td>IP Registered To: </td>{params.value.registeredTo}</tr>
              <tr><td>Tunnels: </td><td>{params.value.tunnels}</td></tr>
            </table>
          </div>
        </center>
      ),
    },
    { field: "region", headerName: "Region", headerClassName: "wsu-theme-header", headerAlign: "center", align: "center", width: 150 },
    { field: "event_time", headerName: "Event Time", headerClassName: "wsu-theme-header", headerAlign: "center", align: "center", width: 250 },
    {
      field: "event_target",
      headerName: "Event Target",
      headerClassName: "wsu-theme-header",
      headerAlign: "center",
      align: "left",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        if (!Array.isArray(params.value)) return "N/A";
        return (
          <div style={{ padding: "8px 12px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.6, width: "100%", boxSizing: "border-box" }}>
            {params.value.map((item, index) => <div key={index}>{item}</div>)}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (!authState || !authState.accessToken) return;

    fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/logs/okta`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: authState.accessToken.accessToken,
      },
      body: JSON.stringify({ userId }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Server error: ${res.statusText}`);
        return res.json();
      })
      .then((result) => {
        const rows = result.logs.map((log) => ({
          id: log.UUID,
          username: log?.["Raw Output"]?.target?.[0]?.alternateId || "N/A",
          event_action: log?.["Display Message"] || "N/A",
          event_actor: log?.Actor?.["Display Name"] || "N/A",
          ip_address: {
            address: log?.["Raw Output"]?.request?.ipChain?.map((chain) => chain.ip)?.join(","),
            isProxy: log?.["Raw Output"].securityContext?.isProxy ? "true" : "false",
            registeredTo: log?.["Raw Output"].securityContext?.asOrg,
            tunnels: log?.["Raw Output"].debugContext?.debugData?.tunnels || "N/A",
          },
          region: log?.["Raw Output"]?.client?.geographicalContext
            ? `${log["Raw Output"].client.geographicalContext.city || ""}, ${log["Raw Output"].client.geographicalContext.state || ""}, ${log["Raw Output"].client.geographicalContext.country || ""}`
            : "N/A",
          event_time: formatDate(log?.Published),
          event_target: log?.["Target"]?.map((target) => `${target.type} - ${target.displayName}`) || [],
        }));

        setState({ data: rows, loaded: true, error: null });
      })
      .catch((error) => setState({ data: null, loaded: true, error }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state.loaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Failed to load Okta log
          </Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      </TableContainer>
    );
  }

  if (!state.data || state.data.length === 0) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Okta Action History
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No records found
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return <FormattedDataGrid columns={columns} rows={state.data} headerText="Okta Action History" />;
};
